/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	forwardRef,
	Fragment,
	useCallback,
	useEffect,
	useState,
	type ForwardRefExoticComponent,
	type RefAttributes,
} from 'react';

import { EngagementSpotlight, useCoordination } from '@atlassiansox/engagekit-ts';
import { css, jsx, keyframes } from '@compiled/react';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { mergeRefs } from 'use-callback-ref';

// eslint-disable-next-line @atlaskit/design-system/no-deprecated-imports
import { PrimaryButton, type PrimaryButtonProps } from '@atlaskit/atlassian-navigation';
import { type ButtonProps } from '@atlaskit/button';
import { type IconButtonProps } from '@atlaskit/button/new';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { RovoIcon } from '@atlaskit/logo';
import { TopNavButton } from '@atlaskit/navigation-system/theming/top-nav-button';
import { fg } from '@atlaskit/platform-feature-flags';
import { PopoverProvider, PopoverTarget } from '@atlaskit/spotlight';
import { token } from '@atlaskit/tokens';
import Tooltip, { type TooltipProps } from '@atlaskit/tooltip';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { SPOTLIGHT_ONBOARDING_TARGET } from '@atlassian/conversation-assistant-onboarding/constants/spotlight';
import { useCoordinationClient } from '@atlassian/conversation-assistant-onboarding/controllers/engagement';
import { usePostOfficeCoordination } from '@atlassian/conversation-assistant-onboarding/controllers/post-office-migration';
import {
	useConversationAssistantSpotlightActions,
	useConversationAssistantSpotlightState,
} from '@atlassian/conversation-assistant-onboarding/controllers/spotlight';
import { ConversationAssistantOnboardingSpotlight } from '@atlassian/conversation-assistant-onboarding/ui/Spotlight';
import { useInsightsStatus } from '@atlassian/rovo-growth-pulse/services/insights-status';

import {
	ConditionalWrapper,
	type ConditionalWrapperChildren,
} from '../../common/ui/conditional-wrapper';

import { messages } from './messages';

type Props = {
	testId?: string;
	isActive: boolean;
	isDisabled?: boolean;
	onClick: ButtonProps['onClick'];
	onMouseEnter?: React.MouseEventHandler<Element>;
	label?: React.ReactNode;
	tooltipContent?: React.ReactNode;
	shortcut?: TooltipProps['shortcut'];
	siteId: string;
	productKey: string;
};

const defaultButtonStyles = {
	paddingInline: token('space.100'),
};

const inactiveButtonStyles = {
	background: token('color.background.neutral'),
};

const disabledButtonStyles = {
	background: token('color.background.disabled'),
	color: token('color.text.disabled'),
};

const conversationAssistantButtonWrapperStyles = css({
	// Override button theme background for both PrimaryButton and Nav4 (TopNavButton), need to disable lint rule for nested styles/selectors because of custom theming possibly colliding
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
	'& button': {
		backgroundColor: token('elevation.surface.overlay'),
		color: token('color.text'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
	'& button:hover': {
		backgroundColor: token('elevation.surface.overlay.hovered'),
		color: token('color.text'),
	},
});

const conversationAssistantButtonWrapperNoOverlayStyles = css({
	// Override button theme background for both PrimaryButton and Nav4 (TopNavButton), need to disable lint rule for nested styles/selectors because of custom theming possibly colliding
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
	'& button': {
		backgroundColor: token('elevation.surface'),
		color: token('color.text'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
	'& button:hover': {
		backgroundColor: token('elevation.surface.hovered'),
		color: token('color.text'),
	},
});

const baseShadow = `0 0 0 2px ${token('color.border.discovery')}`;

const pulseKeyframes = keyframes({
	'0%, 33%': {
		boxShadow: `${baseShadow}, 0 0 0 ${token('color.border.discovery')}`,
	},
	'66%, 100%': {
		boxShadow: `${baseShadow}, 0 0 0 10px rgba(101, 84, 192, 0.01)`,
	},
});

const pulseAnimationStyles = css({
	animationDuration: '3000ms',
	animationIterationCount: 'infinite',
	animationName: pulseKeyframes,
	animationTimingFunction: 'cubic-bezier(0.55, 0.055, 0.675, 0.19)',
	boxShadow: baseShadow,
	borderRadius: token('radius.small'),
});

const reduceMotionStyles = css({
	'@media (prefers-reduced-motion: reduce)': {
		animation: 'none',
		transition: 'none',
	},
});

type ButtonComponentProps = {
	onMouseEnter?: React.MouseEventHandler;
	style?: React.CSSProperties;
	iconBefore?: React.ReactChild;
	testId?: string;
	isSelected?: boolean;
	onClick?: PrimaryButtonProps['onClick'];
	isDisabled?: boolean;
	tooltip?: React.ReactNode;
	shortcut?: TooltipProps['shortcut'];
	children: React.ReactNode;
	interactionName?: string;
};

type HTMLElementForwardedRefComponent = React.ForwardRefExoticComponent<
	ButtonComponentProps & React.RefAttributes<HTMLElement>
>;
type HTMLButtonElementForwardedRefComponent = React.ForwardRefExoticComponent<
	ButtonComponentProps & React.RefAttributes<HTMLButtonElement>
>;

const getConversationAssistantButton = (
	Button: HTMLButtonElementForwardedRefComponent | HTMLElementForwardedRefComponent = PrimaryButton,
) =>
	forwardRef(
		(
			{
				testId,
				isActive,
				isDisabled = false,
				onClick: onClickProp,
				onMouseEnter: onMouseEnterProp,
				label,
				tooltipContent,
				shortcut,
				siteId,
				productKey,
			}: Props,
			ref: React.Ref<HTMLButtonElement>,
		) => {
			di(useCoordination, useCoordinationClient, FeatureGates, useInsightsStatus);
			const { formatMessage } = useIntl();
			const { start } = useConversationAssistantSpotlightActions();

			// Prefetch insights status to warm the cache before the user opens chat.
			// This triggers the backend to generate insights asynchronously if not already cached.
			const isPulseExperimentEnabled = FeatureGates.getExperimentValue(
				'rovo_growth_chat_pulse',
				'isEnabled',
				false,
			);
			useInsightsStatus({
				enabled: isPulseExperimentEnabled,
				assistanceServiceParams: {
					touchpointSource: 'ask-rovo-button-prefetch',
					product: productKey,
					experienceId: 'ai-mate',
					siteId,
				},
			});
			const [shouldShowMessage, setShouldShowMessage] = useState(false);
			const messageId =
				productKey === 'confluence'
					? 'confluence_rovo_chat_agents_spotlight_ga'
					: productKey === 'jira'
						? 'jira_rovo_chat_agents_spotlight_ga'
						: '';
			const shouldUseCoordination = !!messageId;
			const { sendAnalyticsEvent } = useAnalytics();

			const openRovoChat = (
				event: React.MouseEvent<HTMLElement, MouseEvent>,
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				analyticsEvent: any,
			) => {
				if (!isActive && onClick) {
					onClick(event, analyticsEvent);
				}
			};

			const onClick = useCallback<NonNullable<typeof onClickProp>>(
				(...params) => {
					sendAnalyticsEvent({
						eventType: 'ui',
						action: 'clicked',
						actionSubject: 'rovoChatButton',
						attributes: {
							isActive,
							isDisabled,
							productKey,
						},
					});

					return onClickProp?.(...params);
				},
				[isActive, isDisabled, onClickProp, productKey, sendAnalyticsEvent],
			);

			const interactionName = isActive ? 'close-rovo-chat-nav-button' : 'open-rovo-chat-nav-button';
			const buttonLabel = label ?? formatMessage(messages.rovoChatButtonLabel);
			const getChildren = () => {
				const button = (
					<Button
						onMouseEnter={(event) => {
							onMouseEnterProp?.(event);
							sendAnalyticsEvent({
								eventType: 'ui',
								action: 'hovered',
								actionSubject: 'rovoChatButton',
								attributes: {
									isActive,
									isDisabled,
									productKey,
								},
							});
							if (shouldShowMessage) {
								sendAnalyticsEvent({
									eventType: 'ui',
									action: 'hovered',
									actionSubject: 'rovoChatButtonSpotlight',
								});
								start();
							}
						}}
						testId={testId}
						isSelected={isActive}
						onClick={onClick}
						isDisabled={isDisabled}
						tooltip={
							tooltipContent ?? <Fragment>{formatMessage(messages.rovoChatButtonLabel)}</Fragment>
						}
						shortcut={shortcut}
						style={{
							/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766 */
							...defaultButtonStyles,
							...(!isActive && inactiveButtonStyles),
							...(isDisabled && disabledButtonStyles),
							/* eslint-enable @atlaskit/ui-styling-standard/enforce-style-prop */
						}}
						iconBefore={<RovoIcon size="xxsmall" />}
						interactionName={interactionName}
						ref={ref}
					>
						{buttonLabel}
					</Button>
				);

				if (Button === PrimaryButton) {
					return <div role="list">{button}</div>;
				}

				if (!isActive && !isDisabled) {
					const overlayCohort = FeatureGates.getExperimentValue<string>(
						'rovo-chat-nav-button-transparency',
						'backgroundColorCohort',
						'control',
					);

					if (overlayCohort === 'overlay') {
						return <div css={conversationAssistantButtonWrapperStyles}>{button}</div>;
					} else if (overlayCohort === 'no-overlay') {
						return <div css={conversationAssistantButtonWrapperNoOverlayStyles}>{button}</div>;
					}
				}

				return button;
			};

			return shouldUseCoordination ? (
				<SpotlightWrapper
					setShouldShowMessage={setShouldShowMessage}
					messageId={messageId}
					siteId={siteId}
					children={getChildren()}
					shouldShowMessage={shouldShowMessage}
					openRovoChat={openRovoChat}
				/>
			) : (
				getChildren()
			);
		},
	);

const Nav4Button = forwardRef(
	(
		{ tooltip, shortcut, ...props }: ButtonComponentProps,
		forwardedRef: React.Ref<HTMLButtonElement>,
	) => {
		return (
			<Tooltip
				content={tooltip}
				shortcut={shortcut}
				// This button's visible text matches the tooltip
				// Announcing the tooltip means 'Chat' would be announced twice
				isScreenReaderAnnouncementDisabled
			>
				{({ ref: tooltipRef, ...triggerProps }) => (
					<TopNavButton
						interactionName={props?.interactionName}
						ref={mergeRefs([forwardedRef, tooltipRef])}
						{...triggerProps}
						{...props}
						onClick={(event, analyticsEvent) => {
							props.onClick?.(event, analyticsEvent);
							triggerProps.onClick?.(event);
						}}
						iconBefore={(() => props.iconBefore || null) as IconButtonProps['icon']}
					/>
				)}
			</Tooltip>
		);
	},
);

export const ConversationAssistantButton: ForwardRefExoticComponent<
	Props & RefAttributes<HTMLButtonElement>
> = getConversationAssistantButton(PrimaryButton);
export const ConversationAssistantButtonNav4: ForwardRefExoticComponent<
	Props & RefAttributes<HTMLButtonElement>
> = getConversationAssistantButton(Nav4Button);

export const SpotlightWrapper = ({
	children,
	messageId,
	siteId,
	setShouldShowMessage,
	shouldShowMessage,
	openRovoChat,
}: {
	children: ConditionalWrapperChildren;
	messageId: string;
	siteId: string;
	setShouldShowMessage: React.Dispatch<React.SetStateAction<boolean>>;
	shouldShowMessage: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	openRovoChat: (event: React.MouseEvent<HTMLElement, MouseEvent>, analyticsEvent: any) => void;
}): JSX.Element => {
	di(useCoordinationClient, usePostOfficeCoordination);
	const { activeSpotlight } = useConversationAssistantSpotlightState();
	const coordinationClient = useCoordinationClient(siteId);
	const [onboardingStarted, stopOnboarding] = usePostOfficeCoordination(
		coordinationClient,
		messageId,
		fg('rovo-chat-agents-spotlight-post-office-migration'),
	);
	const { sendAnalyticsEvent } = useAnalytics();

	const onDismissMessage = () => {
		setShouldShowMessage(false);
		stopOnboarding();
	};

	useEffect(() => {
		if (shouldShowMessage && activeSpotlight !== null) {
			sendAnalyticsEvent({
				eventType: 'ui',
				action: 'shown',
				actionSubject: 'rovoChatButtonSpotlight',
			});
		}
	}, [shouldShowMessage, activeSpotlight, sendAnalyticsEvent]);

	useEffect(() => {
		if (onboardingStarted) {
			setShouldShowMessage(true);
		}
	}, [onboardingStarted, setShouldShowMessage]);
	return (
		<ConditionalWrapper
			condition={shouldShowMessage}
			wrapper={(children) => (
				<PopoverProvider>
					<PopoverTarget>
						<div css={[activeSpotlight === null && pulseAnimationStyles, reduceMotionStyles]}>
							{children}
							<ConversationAssistantOnboardingSpotlight
								onDismiss={onDismissMessage}
								openRovoChat={openRovoChat}
							/>
							<EngagementSpotlight engagementId={SPOTLIGHT_ONBOARDING_TARGET} />
						</div>
					</PopoverTarget>
				</PopoverProvider>
			)}
		>
			{children}
		</ConditionalWrapper>
	);
};
